import React, { useState } from 'react';
import { Sparkles, AlertTriangle, Lightbulb, CheckCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api';

const READINESS_COLORS = {
  'Not Ready': 'text-red-400 bg-red-900/20 border-red-700/40',
  'Needs Work': 'text-amber-400 bg-amber-900/20 border-amber-700/40',
  'Almost Ready': 'text-blue-400 bg-blue-900/20 border-blue-700/40',
  'Ready to Ship': 'text-green-400 bg-green-900/20 border-green-700/40',
};

export function AIInsightsPanel({ itemId }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchInsights = async () => {
    if (insights) { setExpanded(!expanded); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await api.request('/api/ai/item-insights', {
        method: 'POST',
        body: JSON.stringify({ item_id: itemId }),
      });
      setInsights(data);
      setExpanded(true);
    } catch (e) {
      setError('Failed to generate insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const readinessClass = insights ? (READINESS_COLORS[insights.readiness_label] || READINESS_COLORS['Needs Work']) : '';

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid="ai-insights-panel">
      {/* Header / Trigger */}
      <button
        onClick={fetchInsights}
        disabled={loading}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/15 transition-colors"
        data-testid="ai-insights-trigger"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-primary">AI Insights</span>
          {insights && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${readinessClass}`}>
              {insights.readiness_label}
            </span>
          )}
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : insights ? (
          expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <span className="text-xs text-muted-foreground">Generate</span>
        )}
      </button>

      {/* Content */}
      {error && (
        <div className="px-4 py-3 text-sm text-destructive bg-destructive/5" data-testid="ai-insights-error">{error}</div>
      )}

      {insights && expanded && (
        <div className="p-4 space-y-4 bg-card/30 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="ai-insights-content">
          {/* Readiness Score */}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(insights.readiness_score / 10) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums">{insights.readiness_score}/10</span>
          </div>

          {/* Summary */}
          <div>
            <p className="text-sm text-muted-foreground leading-relaxed">{insights.summary}</p>
          </div>

          {/* Risks */}
          {insights.risks?.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Risks</span>
              </div>
              <ul className="space-y-1.5">
                {insights.risks.map((risk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                    <span className="text-muted-foreground">{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {insights.suggestions?.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wide">Suggestions</span>
              </div>
              <ul className="space-y-1.5">
                {insights.suggestions.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="mt-0.5 h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="text-muted-foreground">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={() => { setInsights(null); setExpanded(false); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="ai-insights-refresh"
          >
            Regenerate
          </button>
        </div>
      )}
    </div>
  );
}
